import React from 'react';
import { TextField as MuiTextField, Typography } from '@mui/material';
import { BaseField } from './BaseField';
import type { BaseFieldProps } from './types';

/**
 * Date field component that handles both:
 * - date (form-builder and legacy)
 */
export const DateField: React.FC<BaseFieldProps> = ({
  field,
  value = '',
  onChange,
  onBlur,
  mode = 'edit',
  error,
  touched,
  sx,
  showLabel = true,
  disabled,
}) => {
  // Extract field properties based on field format
  const label = 'label' in field ? field.label : '';
  const required = 'required' in field ? field.required : false;
  
  // Extract date constraints
  const minDate = 'minDate' in field ? field.minDate : undefined;
  const maxDate = 'maxDate' in field ? field.maxDate : undefined;

  // Format date for display
  const formatDate = (dateValue: string) => {
    if (!dateValue) return '-';
    try {
      const date = new Date(dateValue);
      return date.toLocaleDateString();
    } catch {
      return dateValue;
    }
  };

  // Handle display mode
  if (mode === 'display') {
    return (
      <BaseField field={field} mode={mode} sx={sx} showLabel={showLabel}>
        <Typography variant="body2">{formatDate(value)}</Typography>
      </BaseField>
    );
  }

  // Handle edit/preview modes
  return (
    <BaseField
      field={field}
      mode={mode}
      error={error}
      touched={touched}
      sx={sx}
      showLabel={false}
      disabled={disabled}
    >
      <MuiTextField
        fullWidth
        label={showLabel ? label : undefined}
        type="date"
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onBlur={onBlur}
        disabled={disabled || mode === 'preview'}
        required={required}
        error={!!(touched && error)}
        helperText={touched && error ? error : undefined}
        variant="outlined"
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{
          min: minDate,
          max: maxDate,
        }}
      />
    </BaseField>
  );
};